'use client';

import { HelpCircle } from 'lucide-react';
import React, { useState } from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

import { MetricCard } from './MetricCard';
import { foldMitHeadline, MitKindSummary, type ApprovalCycle } from './MitKindSummary';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChartContainer as ShadcnChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { useTranslation } from 'react-i18next';

interface ApprovalRatesMetricsV4Props {
    data: {
        cit: {
            paymentLevel: {
                withOrchestration: {
                    attempts: number;
                    successes: number;
                    byProcessor: { [processorId: string]: { processorName: string; attempts: number; successes: number } };
                };
                firstAttemptOnly: { attempts: number; successes: number };
            };
            orderLevel: {
                withOrchestration: {
                    attempts: number;
                    successes: number;
                    byProcessor: { [processorId: string]: { processorName: string; attempts: number; successes: number } };
                };
                firstAttemptOnly: { attempts: number; successes: number };
            };
        };
        mit: {
            [chainIndex: string]: {
                withRetries: { attempts: number; successes: number; byProcessor: { [processorId: string]: { processorName: string; attempts: number; successes: number } } };
                firstAttemptOnly: { attempts: number; successes: number };
            };
        };
        citUpsell?: {
            payments: number;
            paymentLevel: { withOrchestration: { attempts: number; successes: number } };
        };
        mitKinds?: {
            subscriptionRebill: { payments: number };
            apiRecurring: { payments: number };
            unscheduledCof: { payments: number };
        };
        apiRecurring?: { [chainIndex: string]: ApprovalCycle };
        unscheduledCof?: ApprovalCycle;
    } | null | undefined;
    isLoading: boolean;
    error: any;
}

function InfoTooltip({ content }: { content: React.ReactNode }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button className="inline-flex items-center justify-center ml-1.5 text-muted-foreground hover:text-foreground transition-colors" type="button">
                    <HelpCircle className="h-4 w-4" />
                </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">{content}</TooltipContent>
        </Tooltip>
    );
}

interface ImpactTableProps {
    title: string;
    firstAttemptLabel: string;
    withImprovementLabel: string;
    firstAttemptRate: number;
    withImprovementRate: number;
    totalAttempts: number;
    firstAttemptSuccesses: number;
    withImprovementSuccesses: number;
    paymentLabel: string;
    tooltipContent?: React.ReactNode;
}

function ImpactTable({ title, firstAttemptLabel, withImprovementLabel, firstAttemptRate, withImprovementRate, totalAttempts, firstAttemptSuccesses, withImprovementSuccesses, paymentLabel, tooltipContent }: ImpactTableProps) {
    const { t } = useTranslation('analytics');
    const improvement = withImprovementRate - firstAttemptRate;
    const recovered = withImprovementSuccesses - firstAttemptSuccesses;
    return (
        <MetricCard title={
            tooltipContent ? (<div className="flex items-center"><span>{title}</span><InfoTooltip content={tooltipContent} /></div>) : title
        } dynamicHeight>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{firstAttemptLabel}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{withImprovementLabel}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.recovered')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="px-4 py-4">
                                <div className="text-2xl font-bold text-foreground">{firstAttemptRate.toFixed(1)}%</div>
                                <div className="text-sm text-muted-foreground mt-1">{t('approvalRates.attemptsAndSucceed', { attempts: totalAttempts.toLocaleString(), paymentLabel, successes: firstAttemptSuccesses.toLocaleString() })}</div>
                            </td>
                            <td className="px-4 py-4">
                                <div className="text-2xl font-bold text-foreground">{withImprovementRate.toFixed(1)}%</div>
                                <div className="text-sm text-muted-foreground mt-1">{t('approvalRates.attemptsAndSucceed', { attempts: totalAttempts.toLocaleString(), paymentLabel, successes: withImprovementSuccesses.toLocaleString() })}</div>
                            </td>
                            <td className="px-4 py-4">
                                <div className="text-2xl font-bold text-foreground">+{improvement.toFixed(1)}%</div>
                                <div className="text-sm text-muted-foreground mt-1">{t('approvalRates.recoveredSucceed', { count: recovered })}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </MetricCard>
    );
}

function CITProcessorTable({ processorAggregates, withOrchestrationData }: { processorAggregates: { id: string; name: string; attempts: number; successes: number; rate: number; }[]; withOrchestrationData: { byProcessor: { [processorId: string]: { processorName: string; attempts: number; successes: number } } } }) {
    const { t } = useTranslation('analytics');
    const tooltipContent = (
        <div className="space-y-2 max-w-md">
            <p className="font-semibold">{t('approvalRates.processorTooltip.title')}</p>
            <p className="opacity-90">
                {t('approvalRates.processorTooltip.description')}
            </p>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold mb-1">{t('approvalRates.processorTooltip.example')}</p>
                <div className="font-mono text-xs bg-muted p-2 rounded">
                    <div>{t('approvalRates.processorTooltip.exampleLine1')}</div>
                    <div>{t('approvalRates.processorTooltip.exampleLine2')}</div>
                    <div>{t('approvalRates.processorTooltip.exampleLine3')}</div>
                </div>
                <div className="mt-2 text-sm">
                    <div><span className="font-semibold">EMS:</span> {t('approvalRates.processorTooltip.emsResult')}</div>
                    <div><span className="font-semibold">PayArc:</span> {t('approvalRates.processorTooltip.payarcResult')}</div>
                </div>
            </div>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold">{t('approvalRates.processorTooltip.formula')}</p>
                <p className="opacity-90 text-sm">
                    {t('approvalRates.processorTooltip.formulaText')}
                </p>
            </div>
        </div>
    );
    return (
        <MetricCard title={<div className="flex items-center"><span>{t('approvalRates.processorTable.title')}</span><InfoTooltip content={tooltipContent} /></div>} description={t('approvalRates.processorTable.description')} dynamicHeight>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.processorTable.processor')}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.processorTable.success')}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.processorTable.failed')}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.processorTable.approvalRate')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {processorAggregates.sort((a, b) => a.name.localeCompare(b.name)).map((processor) => {
                            const withOrchestration = withOrchestrationData.byProcessor[processor.id];
                            if (!withOrchestration) return null;
                            const withOrchestrationRate = withOrchestration.attempts > 0 ? (withOrchestration.successes / withOrchestration.attempts) * 100 : 0;
                            const withOrchestrationFailed = withOrchestration.attempts - withOrchestration.successes;
                            return (
                                <tr key={processor.id}>
                                    <td className="px-4 py-4 text-sm font-medium text-foreground">{processor.name}</td>
                                    <td className="px-4 py-4 text-sm text-foreground">{withOrchestration.successes.toLocaleString()}</td>
                                    <td className="px-4 py-4 text-sm text-foreground">{withOrchestrationFailed.toLocaleString()}</td>
                                    <td className="px-4 py-4"><div className="text-base font-semibold text-foreground">{withOrchestrationRate.toFixed(1)}%</div></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </MetricCard>
    );
}

function MITApprovalByCycleChart({ cycleData, title, description }: { cycleData: { cycle: number; withRetriesRate: number; firstAttemptRate: number; withRetriesAttempts: number; withRetriesSuccesses: number; firstAttemptAttempts: number; firstAttemptSuccesses: number; }[]; title?: string; description?: string }) {
    const { t } = useTranslation('analytics');
    const chartData = cycleData.map((cycle) => {
        const improvement = cycle.withRetriesRate - cycle.firstAttemptRate;
        return { cycle: t('approvalRates.cycle', { number: cycle.cycle }), firstAttempt: cycle.firstAttemptRate, retryImprovement: improvement > 0 ? improvement : 0, totalRate: cycle.withRetriesRate, withRetriesAttempts: cycle.withRetriesAttempts, withRetriesSuccesses: cycle.withRetriesSuccesses, firstAttemptAttempts: cycle.firstAttemptAttempts, firstAttemptSuccesses: cycle.firstAttemptSuccesses };
    });
    return (
        <MetricCard title={title ?? t('approvalRates.byCycleChart.title')} description={description ?? t('approvalRates.byCycleChart.description')} dynamicHeight>
            <div className="h-80 mb-2">
                <ShadcnChartContainer
                    config={{
                        firstAttempt: { label: t('approvalRates.firstAttemptChartLabel'), color: 'var(--chart-1)' },
                        retryImprovement: { label: t('approvalRates.retryImprovementChartLabel'), color: 'var(--chart-2)' },
                    }}
                    className="w-full h-full"
                >
                    <BarChart accessibilityLayer data={chartData}>
                        <XAxis dataKey="cycle" tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                        <Bar dataKey="firstAttempt" stackId="a" fill="var(--color-firstAttempt)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="retryImprovement" stackId="a" fill="var(--color-retryImprovement)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ShadcnChartContainer>
            </div>
        </MetricCard>
    );
}

function MITOverallProcessorTable({ processorAggregates }: { processorAggregates: { id: string; name: string; attempts: number; successes: number; rate: number; }[] }) {
    const { t } = useTranslation('analytics');
    const tooltipContent = (
        <div className="space-y-2 max-w-md">
            <p className="font-semibold">{t('approvalRates.mitOverallTooltip.title')}</p>
            <p className="opacity-90">
                {t('approvalRates.mitOverallTooltip.description')}
            </p>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold mb-1">{t('approvalRates.mitOverallTooltip.example')}</p>
                <div className="font-mono text-xs bg-muted p-2 rounded">
                    <div>{t('approvalRates.mitOverallTooltip.exampleLine1')}</div>
                    <div>{t('approvalRates.mitOverallTooltip.exampleLine2')}</div>
                    <div>{t('approvalRates.mitOverallTooltip.exampleLine3')}</div>
                </div>
                <div className="mt-2 text-sm">
                    <div><span className="font-semibold">EMS:</span> {t('approvalRates.mitOverallTooltip.emsResult')}</div>
                    <div><span className="font-semibold">PayArc:</span> {t('approvalRates.mitOverallTooltip.payarcResult')}</div>
                </div>
            </div>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold">{t('approvalRates.mitOverallTooltip.formula')}</p>
                <p className="opacity-90 text-sm">
                    {t('approvalRates.mitOverallTooltip.formulaText')}
                </p>
            </div>
        </div>
    );
    return (
        <MetricCard title={<div className="flex items-center"><span>{t('approvalRates.mitOverallProcessor.title')}</span><InfoTooltip content={tooltipContent} /></div>} description={t('approvalRates.mitOverallProcessor.description')} dynamicHeight>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.mitOverallProcessor.processor')}</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{t('approvalRates.mitOverallProcessor.approvalRate')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {processorAggregates.sort((a, b) => a.name.localeCompare(b.name)).map((processor) => {
                            const failed = processor.attempts - processor.successes;
                            return (
                                <tr key={processor.id}>
                                    <td className="px-4 py-4 text-sm font-medium text-foreground">{processor.name}</td>
                                    <td className="px-4 py-4">
                                        <div className="text-base font-semibold text-foreground">{processor.rate.toFixed(1)}%</div>
                                        <div className="text-sm text-muted-foreground mt-1">{t('approvalRates.mitOverallProcessor.succeedAndFailed', { successes: processor.successes.toLocaleString(), failed: failed.toLocaleString() })}</div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </MetricCard>
    );
}

function MITProcessorByCycleTable({ cycleData }: { cycleData: { cycle: number; byProcessor: { [processorId: string]: { processorName: string; attempts: number; successes: number } } }[] }) {
    const { t } = useTranslation('analytics');
    const allProcessors = new Map<string, string>();
    cycleData.forEach((cycle) => { Object.entries(cycle.byProcessor).forEach(([id, processor]) => { if (!allProcessors.has(id)) { allProcessors.set(id, processor.processorName); } }); });
    const tooltipContent = (
        <div className="space-y-2 max-w-md">
            <p className="font-semibold">{t('approvalRates.processorByCycleTooltip.title')}</p>
            <p className="opacity-90">
                {t('approvalRates.processorByCycleTooltip.description')}
            </p>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold mb-1">{t('approvalRates.processorByCycleTooltip.whyMatters')}</p>
                <div className="text-sm opacity-90">
                    <p>{t('approvalRates.processorByCycleTooltip.whyMattersText')}</p>
                </div>
            </div>

            <div className="mt-3 pt-2 border-t border-border">
                <p className="font-semibold">{t('approvalRates.processorByCycleTooltip.exampleUsage')}</p>
                <p className="text-sm opacity-90">
                    {t('approvalRates.processorByCycleTooltip.exampleUsageText')}
                </p>
            </div>
        </div>
    );
    return (
        <MetricCard title={<div className="flex items-center"><span>{t('approvalRates.processorByCycle.title')}</span><InfoTooltip content={tooltipContent} /></div>} description={t('approvalRates.processorByCycle.description')} dynamicHeight>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground sticky left-0 bg-card z-10">{t('approvalRates.processorByCycle.processor')}</th>
                            {cycleData.map((cycle) => (<th key={cycle.cycle} className="px-4 py-3 text-center text-sm font-semibold text-foreground">{cycle.cycle}</th>))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {Array.from(allProcessors.entries()).sort(([, a], [, b]) => a.localeCompare(b)).map(([processorId, processorName]) => (
                            <tr key={processorId}>
                                <td className="px-4 py-4 text-sm font-medium text-foreground sticky left-0 bg-card">{processorName}</td>
                                {cycleData.map((cycle) => {
                                    const processor = cycle.byProcessor[processorId];
                                    if (!processor) return (<td key={cycle.cycle} className="px-4 py-4 text-center text-sm text-muted-foreground">—</td>);
                                    const rate = processor.attempts > 0 ? (processor.successes / processor.attempts) * 100 : 0;
                                    return (<td key={cycle.cycle} className="px-4 py-4 text-center text-sm font-semibold text-foreground">{rate.toFixed(1)}%</td>);
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </MetricCard>
    );
}

export function ApprovalRatesMetricsV4({ data, isLoading, error }: ApprovalRatesMetricsV4Props) {
    const { t } = useTranslation('analytics');
    const [transactionType, setTransactionType] = useState<'cit' | 'mit'>('cit');
    const [consolidateByOrder, setConsolidateByOrder] = useState<boolean>(true);

    const metrics = React.useMemo(() => {
        if (!data) {
            return { primary: { overallRate: 0, totalAttempts: 0, totalSuccesses: 0, bestProcessor: null, processorAggregates: [] as any[] }, firstAttempt: { overallRate: 0, totalAttempts: 0, totalSuccesses: 0, bestProcessor: null, processorAggregates: [] as any[] } };
        }
        if (transactionType === 'cit') {
            const citData = consolidateByOrder ? data.cit.orderLevel : data.cit.paymentLevel;
            const withOrchestrationData = citData.withOrchestration;
            const primaryTotalAttempts = withOrchestrationData.attempts;
            const primaryTotalSuccesses = withOrchestrationData.successes;
            const primaryOverallRate = primaryTotalAttempts > 0 ? (primaryTotalSuccesses / primaryTotalAttempts) * 100 : 0;
            const processorAggregates = Object.entries(withOrchestrationData.byProcessor).map(([id, processor]) => ({ id, name: processor.processorName, attempts: processor.attempts, successes: processor.successes, rate: processor.attempts > 0 ? (processor.successes / processor.attempts) * 100 : 0, })).sort((a, b) => b.rate - a.rate);
            const firstAttemptData = citData.firstAttemptOnly;
            const firstAttemptTotalAttempts = firstAttemptData.attempts;
            const firstAttemptTotalSuccesses = firstAttemptData.successes;
            const firstAttemptOverallRate = firstAttemptTotalAttempts > 0 ? (firstAttemptTotalSuccesses / firstAttemptTotalAttempts) * 100 : 0;
            return { primary: { overallRate: primaryOverallRate, totalAttempts: primaryTotalAttempts, totalSuccesses: primaryTotalSuccesses, bestProcessor: processorAggregates[0] || null, processorAggregates }, firstAttempt: { overallRate: firstAttemptOverallRate, totalAttempts: firstAttemptTotalAttempts, totalSuccesses: firstAttemptTotalSuccesses, bestProcessor: null, processorAggregates: [] } };
        } else {
            return foldMitHeadline(data);
        }
    }, [data, transactionType, consolidateByOrder]);

    if (error) {
        return (
            <div className="grid grid-cols-1 gap-6">
                <MetricCard title={t('approvalRates.title')} description={t('approvalRates.errorDescription')} className="col-span-full">
                    <div className="flex items-center justify-center h-full"><p className="text-destructive">{t('approvalRates.errorMessage')}</p></div>
                </MetricCard>
            </div>
        );
    }

    const mitCycleData = (transactionType === 'mit' && data) ? Object.entries(data.mit).map(([chainIndex, cycle]) => {
        const withRetriesRate = cycle.withRetries.attempts > 0 ? (cycle.withRetries.successes / cycle.withRetries.attempts) * 100 : 0;
        const firstAttemptRate = cycle.firstAttemptOnly.attempts > 0 ? (cycle.firstAttemptOnly.successes / cycle.firstAttemptOnly.attempts) * 100 : 0;
        return { cycle: parseInt(chainIndex), withRetriesRate, firstAttemptRate, withRetriesAttempts: cycle.withRetries.attempts, withRetriesSuccesses: cycle.withRetries.successes, firstAttemptAttempts: cycle.firstAttemptOnly.attempts, firstAttemptSuccesses: cycle.firstAttemptOnly.successes, byProcessor: cycle.withRetries.byProcessor };
    }).sort((a, b) => a.cycle - b.cycle) : [];

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-10 w-80 bg-muted rounded-lg"></div>
                <div className="rounded-lg border border-border bg-card shadow-sm p-5"><div className="mb-4 h-6 w-48 bg-muted rounded"></div><div className="h-48 bg-muted rounded"></div></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Tabs value={transactionType} onValueChange={(value) => setTransactionType(value as 'cit' | 'mit')}>
                <TabsList>
                    <TabsTrigger value="cit">{t('approvalRates.citTab')}</TabsTrigger>
                    <TabsTrigger value="mit">{t('approvalRates.mitTab')}</TabsTrigger>
                </TabsList>
            </Tabs>

            {transactionType === 'cit' && data && (
                <>
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/50">
                        <input type="checkbox" id="consolidate-order" checked={consolidateByOrder} onChange={(e) => setConsolidateByOrder(e.target.checked)} className="h-4 w-4 rounded border-border text-primary" />
                        <label htmlFor="consolidate-order" className="text-sm font-medium text-foreground cursor-pointer select-none">{t('approvalRates.consolidateLabel')}</label>
                    </div>

                    <ImpactTable
                        title={t('approvalRates.orchestrationImpact')}
                        firstAttemptLabel={t('approvalRates.firstAttempt')}
                        withImprovementLabel={t('approvalRates.withOrchestration')}
                        firstAttemptRate={metrics.firstAttempt.overallRate}
                        withImprovementRate={metrics.primary.overallRate}
                        totalAttempts={metrics.primary.totalAttempts}
                        firstAttemptSuccesses={metrics.firstAttempt.totalSuccesses}
                        withImprovementSuccesses={metrics.primary.totalSuccesses}
                        paymentLabel={consolidateByOrder ? t('approvalRates.orders') : t('approvalRates.payments')}
                        tooltipContent={
                            <div className="space-y-2 max-w-md">
                                <p className="font-semibold">{t('approvalRates.orchestrationTooltip.title')}</p>
                                <p className="opacity-90">
                                    {t('approvalRates.orchestrationTooltip.description')}
                                </p>

                                <div className="mt-3 pt-2 border-t border-border">
                                    <p className="font-semibold mb-1">{t('approvalRates.orchestrationTooltip.example')}</p>
                                    <div className="font-mono text-xs bg-muted p-2 rounded">
                                        <div>{t('approvalRates.orchestrationTooltip.exampleLine1')}</div>
                                        <div>{t('approvalRates.orchestrationTooltip.exampleLine2')}</div>
                                        <div>{t('approvalRates.orchestrationTooltip.exampleLine3')}</div>
                                    </div>
                                    <div className="mt-2 text-sm">
                                        <div><span className="font-semibold">{t('approvalRates.orchestrationTooltip.firstAttemptResult')}</span> {t('approvalRates.orchestrationTooltip.firstAttemptValue')}</div>
                                        <div><span className="font-semibold">{t('approvalRates.orchestrationTooltip.withOrchestrationResult')}</span> {t('approvalRates.orchestrationTooltip.withOrchestrationValue')}</div>
                                        <div><span className="font-semibold">{t('approvalRates.orchestrationTooltip.recoveredResult')}</span> {t('approvalRates.orchestrationTooltip.recoveredValue')}</div>
                                    </div>
                                </div>

                                <div className="mt-3 pt-2 border-t border-border">
                                    <p className="font-semibold">{t('approvalRates.orchestrationTooltip.formula')}</p>
                                    <p className="opacity-90 text-sm">
                                        {t('approvalRates.orchestrationTooltip.formulaText')}
                                    </p>
                                </div>
                            </div>
                        }
                    />
                    <CITProcessorTable processorAggregates={metrics.primary.processorAggregates} withOrchestrationData={consolidateByOrder ? data.cit.orderLevel.withOrchestration : data.cit.paymentLevel.withOrchestration} />
                    {data.citUpsell && data.citUpsell.payments > 0 && (
                        <MetricCard title={t('approvalRates.citUpsellTitle')} description={t('approvalRates.citUpsellDescription')}>
                            <p className="text-2xl font-semibold text-foreground">
                                {data.citUpsell.paymentLevel.withOrchestration.attempts > 0
                                    ? ((data.citUpsell.paymentLevel.withOrchestration.successes / data.citUpsell.paymentLevel.withOrchestration.attempts) * 100).toFixed(1)
                                    : '0.0'}%
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">{t('approvalRates.kindPayments', { payments: data.citUpsell.payments.toLocaleString() })}</p>
                        </MetricCard>
                    )}
                </>
            )}

            {transactionType === 'mit' && data && (
                <>
                    <MitKindSummary mit={data.mit} apiRecurring={data.apiRecurring} unscheduledCof={data.unscheduledCof} kinds={data.mitKinds} />
                    <ImpactTable
                        title={t('approvalRates.retryImpact')}
                        firstAttemptLabel={t('approvalRates.firstAttempt')}
                        withImprovementLabel={t('approvalRates.withRetries')}
                        firstAttemptRate={metrics.firstAttempt.overallRate}
                        withImprovementRate={metrics.primary.overallRate}
                        totalAttempts={metrics.primary.totalAttempts}
                        firstAttemptSuccesses={metrics.firstAttempt.totalSuccesses}
                        withImprovementSuccesses={metrics.primary.totalSuccesses}
                        paymentLabel={t('approvalRates.rebills')}
                        tooltipContent={
                            <div className="space-y-2 max-w-md">
                                <p className="font-semibold">{t('approvalRates.retryTooltip.title')}</p>
                                <p className="opacity-90">
                                    {t('approvalRates.retryTooltip.description')}
                                </p>

                                <div className="mt-3 pt-2 border-t border-border">
                                    <p className="font-semibold mb-1">{t('approvalRates.retryTooltip.example')}</p>
                                    <div className="font-mono text-xs bg-muted p-2 rounded">
                                        <div>{t('approvalRates.retryTooltip.exampleLine1')}</div>
                                        <div>{t('approvalRates.retryTooltip.exampleLine2')}</div>
                                        <div>{t('approvalRates.retryTooltip.exampleLine3')}</div>
                                    </div>
                                    <div className="mt-2 text-sm">
                                        <div><span className="font-semibold">{t('approvalRates.retryTooltip.firstAttemptResult')}</span> {t('approvalRates.retryTooltip.firstAttemptValue')}</div>
                                        <div><span className="font-semibold">{t('approvalRates.retryTooltip.withRetriesResult')}</span> {t('approvalRates.retryTooltip.withRetriesValue')}</div>
                                        <div><span className="font-semibold">{t('approvalRates.retryTooltip.recoveredResult')}</span> {t('approvalRates.retryTooltip.recoveredValue')}</div>
                                    </div>
                                </div>

                                <div className="mt-3 pt-2 border-t border-border">
                                    <p className="font-semibold">{t('approvalRates.retryTooltip.formula')}</p>
                                    <p className="opacity-90 text-sm">
                                        {t('approvalRates.retryTooltip.formulaText')}
                                    </p>
                                </div>
                            </div>
                        }
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <MITApprovalByCycleChart cycleData={mitCycleData} />
                        <MITOverallProcessorTable processorAggregates={metrics.primary.processorAggregates} />
                    </div>
                    {data.apiRecurring && Object.keys(data.apiRecurring).length > 0 && (
                        <MITApprovalByCycleChart
                            cycleData={Object.entries(data.apiRecurring).map(([chainIndex, cycle]) => {
                                const withRetriesRate = cycle.withRetries.attempts > 0 ? (cycle.withRetries.successes / cycle.withRetries.attempts) * 100 : 0;
                                const firstAttemptRate = cycle.firstAttemptOnly.attempts > 0 ? (cycle.firstAttemptOnly.successes / cycle.firstAttemptOnly.attempts) * 100 : 0;
                                return { cycle: parseInt(chainIndex), withRetriesRate, firstAttemptRate, withRetriesAttempts: cycle.withRetries.attempts, withRetriesSuccesses: cycle.withRetries.successes, firstAttemptAttempts: cycle.firstAttemptOnly.attempts, firstAttemptSuccesses: cycle.firstAttemptOnly.successes };
                            }).sort((a, b) => a.cycle - b.cycle)}
                            title={t('approvalRates.apiRecurringChart')}
                            description={t('approvalRates.apiRecurringChartDescription')}
                        />
                    )}
                    <MITProcessorByCycleTable cycleData={mitCycleData} />
                </>
            )}
        </div>
    );
}


